// src/components/LoginPage.js
// Datatech Solutions branded login screen.
// Matches the light theme, Inter font, and gradient from styles.css.

import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../auth/msalConfig";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    background: #f8f9fa;
    font-family: 'Inter', sans-serif;
  }

  .login-wrap {
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #2a4365 0%, #0b80a5 100%);
    position: relative;
    overflow: hidden;
  }

  /* Subtle background pattern */
  .login-wrap::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(255,255,255,0.06) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(255,255,255,0.04) 0%, transparent 50%);
    pointer-events: none;
  }

  .login-card {
    background: white;
    border-radius: 16px;
    padding: 48px 44px;
    width: 420px;
    text-align: center;
    box-shadow: 0 24px 60px rgba(0,0,0,0.25), 0 4px 12px rgba(0,0,0,0.1);
    position: relative;
    z-index: 1;
  }

  /* Datatech gradient top bar */
  .login-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, #2a4365, #0b80a5);
    border-radius: 16px 16px 0 0;
  }

  .login-logo {
    width: 56px; height: 56px;
    border-radius: 12px;
    background: linear-gradient(135deg, #2a4365, #0b80a5);
    display: flex; align-items: center; justify-content: center;
    font-size: 24px; font-weight: 700; color: white;
    font-family: 'Inter', sans-serif;
    margin: 0 auto 20px;
    box-shadow: 0 8px 20px rgba(11,128,165,0.35);
  }

  .login-company {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #0b80a5;
    margin-bottom: 8px;
  }

  .login-title {
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    font-size: 22px;
    letter-spacing: -0.4px;
    color: #202020;
    margin-bottom: 10px;
  }

  .login-sub {
    font-size: 13px;
    color: #838383;
    line-height: 1.7;
    margin-bottom: 36px;
  }

  .login-divider {
    height: 1px;
    background: #f0f0f0;
    margin: 0 0 28px;
  }

  .login-btn {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #2a4365, #0b80a5);
    border: none;
    border-radius: 8px;
    color: white;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    position: relative;
    overflow: hidden;
  }

  .login-btn::after {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 100%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
    transition: left 0.7s;
  }

  .login-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(11,128,165,0.4);
  }

  .login-btn:hover::after { left: 100%; }

  .login-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .login-btn:disabled::after { display: none; }

  .login-error {
    font-size: 12px;
    color: #e74c3c;
    background: rgba(231,76,60,0.06);
    border: 1px solid rgba(231,76,60,0.2);
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 16px;
    text-align: left;
  }

  .login-note {
    font-size: 11px;
    color: #c8d2da;
    margin-top: 18px;
    line-height: 1.6;
  }

  .login-features {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-top: 32px;
    flex-wrap: wrap;
  }

  .login-feature {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: rgba(255,255,255,0.65);
  }

  .login-feature-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: rgba(255,255,255,0.4);
  }

  .login-spinner {
    width: 16px; height: 16px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
`;

export default function LoginPage() {
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      setError("Sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="login-wrap">
        <div className="login-card">
          <div className="login-logo">D</div>
          <div className="login-company">Datatech Solutions</div>
          <div className="login-title">MSP Operating System</div>
          <div className="login-sub">
            Your daily operating platform for focus sessions, client meetings,
            inbox triage, and Autotask ticket management.
          </div>
          <div className="login-divider"/>
          {error && <div className="login-error">⚠ {error}</div>}
          <button
            className="login-btn"
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="login-spinner"/>
                Redirecting to Microsoft...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
                  <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
                  <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft 365
              </>
            )}
          </button>
          <div className="login-note">
            Your credentials are handled entirely by Microsoft.<br/>
            This app never sees your password.
          </div>
        </div>

        {/* Feature pills below the card */}
        <div className="login-features">
          {["M365 Inbox","Calendar & Prep","Autotask PSA","Fellow Notes","AI Briefs"].map(f=>(
            <div key={f} className="login-feature">
              <div className="login-feature-dot"/>
              {f}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}